import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface CategoryCard {
  slug: string;
  titleKey: string;
  descKey: string;
  countKey: string;
  icon: string;
  image: string;
  accent: string;
}

const CATEGORIES: CategoryCard[] = [
  {
    slug: "historical",
    titleKey: "cat_historical_title",
    descKey: "cat_historical_desc",
    countKey: "cat_historical_count",
    icon: "ri-ancient-pavilion-line",
    image: "https://public.readdy.ai/ai/img_res/29cd739b43ae51ffaa255548c6cf3629.jpg",
    accent: "from-stone-900/80",
  },
  {
    slug: "food",
    titleKey: "cat_food_title",
    descKey: "cat_food_desc",
    countKey: "cat_food_count",
    icon: "ri-restaurant-line",
    image: "https://readdy.ai/api/search-image?query=Turkish%20food%20iskender%20kebab%20traditional%20cuisine%20restaurant%20warm%20tones%20delicious%20authentic%20Bursa%20gastronomy&width=400&height=280&seq=cat_food_001&orientation=landscape",
    accent: "from-forest-900/80",
  },
  {
    slug: "entertainment",
    titleKey: "cat_entertainment_title",
    descKey: "cat_entertainment_desc",
    countKey: "cat_entertainment_count",
    icon: "ri-gamepad-line",
    image: "https://readdy.ai/api/search-image?query=adventure%20park%20zipline%20mountain%20sled%20fun%20outdoor%20activities%20Turkey%20forest%20green%20summer%20entertainment&width=400&height=280&seq=cat_ent_001&orientation=landscape",
    accent: "from-anthracite-900/80",
  },
  {
    slug: "accommodation",
    titleKey: "cat_accommodation_title",
    descKey: "cat_accommodation_desc",
    countKey: "cat_accommodation_count",
    icon: "ri-hotel-line",
    image: "https://readdy.ai/api/search-image?query=boutique%20thermal%20hotel%20spa%20luxury%20Bursa%20Turkey%20elegant%20interior%20warm%20stone%20tones%20wellness%20peaceful%20room&width=400&height=280&seq=cat_hotel_001&orientation=landscape",
    accent: "from-stone-900/80",
  },
  {
    slug: "nature",
    titleKey: "cat_nature_title",
    descKey: "cat_nature_desc",
    countKey: "cat_nature_count",
    icon: "ri-plant-line",
    image: "https://readdy.ai/api/search-image?query=Uludag%20mountain%20nature%20trail%20forest%20path%20Bursa%20Turkey%20green%20trees%20lake%20panoramic%20natural%20landscape%20hiking&width=400&height=280&seq=cat_nature_001&orientation=landscape",
    accent: "from-forest-900/80",
  },
  {
    slug: "sightseeing",
    titleKey: "cat_sightseeing_title",
    descKey: "cat_sightseeing_desc",
    countKey: "cat_sightseeing_count",
    icon: "ri-eye-line",
    image: "https://readdy.ai/api/search-image?query=Cumalikizik%20village%20cobblestone%20Ottoman%20houses%20museum%20bazaar%20sightseeing%20Bursa%20Turkey%20heritage%20tourism&width=400&height=280&seq=cat_sight_001&orientation=landscape",
    accent: "from-anthracite-900/80",
  },
  {
    slug: "featured",
    titleKey: "cat_featured_title",
    descKey: "cat_featured_desc",
    countKey: "cat_featured_count",
    icon: "ri-star-line",
    image: "https://readdy.ai/api/search-image?query=Bursa%20best%20top%20rated%20venues%20editor%20picks%20premium%20selection%20city%20guide%20highlights%20scenic%20panorama&width=400&height=280&seq=cat_feat_001&orientation=landscape",
    accent: "from-forest-900/80",
  },
];

export default function CategoriesSection() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-28 bg-cream-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-forest-100 text-forest-700 text-xs font-semibold font-body uppercase tracking-wider mb-4">
            {t("cat_section_badge")}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-anthracite-900 mb-4">
            {t("cat_section_title")}
          </h2>
          <p className="text-anthracite-500 text-base md:text-lg max-w-2xl mx-auto font-body leading-relaxed">
            {t("cat_section_subtitle")}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {CATEGORIES.map((cat, index) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`group relative overflow-hidden rounded-2xl cursor-pointer block ${
                index === 0 ? "sm:col-span-2 sm:row-span-2" : ""
              }`}
              style={{ minHeight: index === 0 ? "360px" : "200px" }}
            >
              {/* Image */}
              <img
                src={cat.image}
                alt={t(cat.titleKey)}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
              />

              {/* Gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-t ${cat.accent} to-transparent`} />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                    <i className={`${cat.icon} text-white text-base`} />
                  </div>
                  <span className="text-white/60 text-xs font-body">{t(cat.countKey)}</span>
                </div>
                <h3 className={`font-display font-bold text-white mb-1 ${index === 0 ? "text-2xl md:text-3xl" : "text-lg"}`}>
                  {t(cat.titleKey)}
                </h3>
                <p className={`text-white/70 font-body leading-snug ${index === 0 ? "text-sm" : "text-xs"}`}>
                  {t(cat.descKey)}
                </p>

                {/* Arrow */}
                <div className="mt-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/0 group-hover:bg-white/20 transition-all opacity-0 group-hover:opacity-100">
                  <i className="ri-arrow-right-line text-white text-sm" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
